import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@apollo/client/react';

import {
  AppointmentQuote,
  AppointmentQuoteCreateDocument,
  LaborRate,
  Maybe,
  PackageSetEntryFragment,
  PricingSetFragment,
  Pricing__QuotableEnum,
  Pricing__SourceEnum,
} from '@graphql/platform';

import { PLANS } from '@root/constants/pricing';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';
import { PlanName } from '@root/resources/types/plan_name';

import { extractRateGroup } from '@utils/pricing/pricing_set';

export type AppointmentQuoteCreateResult = {
  laborRate?: LaborRate;
  onboardingPackageSetEntry?: PackageSetEntryFragment;
  gasFeeAmount?: number | null;
  finalPackageSetEntry?: PackageSetEntryFragment;
  packageSetEntries: PackageSetEntryFragment[];
  appointmentQuote?: Maybe<AppointmentQuote>;
};

type AppointmentQuoteCreatePayload = {
  laborRate?: LaborRate;
  onboardingPackageSetEntries: PackageSetEntryFragment[];
  finalPackageSetEntries: PackageSetEntryFragment[];
  gasFeeAmount?: number | null;
  quote?: Maybe<AppointmentQuote>;
};

function quoteResultFromPayload(
  data: AppointmentQuoteCreatePayload,
): AppointmentQuoteCreateResult {
  const {
    laborRate,
    onboardingPackageSetEntries,
    finalPackageSetEntries,
    gasFeeAmount,
    quote,
  } = data;
  const onboardingPackageSetEntry = onboardingPackageSetEntries[0];
  const finalPackageSetEntry = finalPackageSetEntries[0];
  const packageSetEntries = onboardingPackageSetEntries.concat(
    finalPackageSetEntries,
  );
  return {
    laborRate,
    appointmentQuote: quote,
    onboardingPackageSetEntry,
    gasFeeAmount,
    finalPackageSetEntry,
    packageSetEntries,
  };
}

// This hook will automatically execute the mutation when its inputs change.
export const useAppointmentQuoteCreate = ({
  pricingSet,
  zip,
  rateGroupName,
  planSize,
  customerToken,
}: {
  pricingSet?: PricingSetFragment;
  zip?: string;
  rateGroupName?: string;
  planSize?: PlanName;
  customerToken?: string;
}): {
  quote: AppointmentQuoteCreateResult;
  loading: boolean;
  /** Set when the mutation promise rejects (e.g. network); success path omits this. */
  quoteError?: unknown;
} => {
  const rateGroup = extractRateGroup(rateGroupName, pricingSet);
  const cuft = planSize ? PLANS[planSize as PlanName]?.cuft : undefined;
  const quotableId = customerToken ? customerToken : WT_VISITOR_TOKEN;
  const quotableType = customerToken
    ? Pricing__QuotableEnum.Customer
    : Pricing__QuotableEnum.Visitor;
  const source = customerToken
    ? Pricing__SourceEnum.LandingReonboarding
    : Pricing__SourceEnum.Landing;

  const inputsKey = useMemo(() => {
    if (
      zip === undefined ||
      cuft === undefined ||
      rateGroup === undefined ||
      !rateGroup.id
    ) {
      return '';
    }
    return `${zip}|${cuft}|${rateGroup.id}|${quotableId}|${quotableType}|${source}`;
  }, [zip, cuft, rateGroup, quotableId, quotableType, source]);

  const inputsKeyRef = useRef(inputsKey);
  inputsKeyRef.current = inputsKey;
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [settled, setSettled] = useState<{
    key: string;
    quote: AppointmentQuoteCreateResult;
    error?: unknown;
  } | null>(null);

  useLayoutEffect(() => {
    if (!inputsKey) return;
    setSettled(null);
  }, [inputsKey]);

  const [appointmentQuoteCreate] = useMutation(AppointmentQuoteCreateDocument, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (cuft === undefined) return;
    if (rateGroup === undefined) return;
    if (zip === undefined) return;

    const runKey = inputsKey;
    if (!runKey) return;

    void appointmentQuoteCreate({
      variables: {
        zip: zip,
        cuft: cuft,
        rateGroupId: rateGroup.id,
        quotableId: quotableId,
        quotableType: quotableType,
        source: source,
      },
    })
      .then((mutationResult) => {
        if (inputsKeyRef.current !== runKey || !isMountedRef.current) return;
        const raw = mutationResult.data?.appointmentQuoteCreate as
          | AppointmentQuoteCreatePayload
          | undefined
          | null;
        if (raw == null) {
          setSettled({
            key: runKey,
            quote: { packageSetEntries: [] },
            error: new Error('appointmentQuoteCreate returned no data'),
          });
          return;
        }
        setSettled({ key: runKey, quote: quoteResultFromPayload(raw) });
      })
      .catch((error: unknown) => {
        if (inputsKeyRef.current !== runKey || !isMountedRef.current) return;
        setSettled({
          key: runKey,
          quote: { packageSetEntries: [] },
          error,
        });
      });
  }, [
    appointmentQuoteCreate,
    cuft,
    inputsKey,
    quotableId,
    quotableType,
    rateGroup,
    source,
    zip,
  ]);

  if (!inputsKey || !settled || settled.key !== inputsKey) {
    return { quote: { packageSetEntries: [] }, loading: true };
  }

  return settled.error !== undefined
    ? {
        quote: settled.quote,
        loading: false,
        quoteError: settled.error,
      }
    : { quote: settled.quote, loading: false };
};
